import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell, PageHero } from "@/components/marketing/PageShell";
import { CUSTOMERS } from "@/content/pages";

export default function Customers() {
  const { featured, quotes, logos } = CUSTOMERS;
  return (
    <PageShell testid="customers-root" seo={{
      title: "Customers — Sema",
      description: "How data teams use Sema to give their organization governed, plain-English answers they can trust.",
      keywords: "sema customers, case studies, customer stories",
      path: "/customers",
    }}>
      <PageHero testid="customers-h1" eyebrow="Customers"
        title="Teams that trust the number."
        subtitle="From senior-living operators to fintech risk teams, data leaders use Sema to make AI answers correct, explainable and safe." />

      {/* Featured */}
      <section className="pb-16">
        <div className="mx-auto w-full max-w-5xl px-6">
          <div data-testid="customers-featured" className="rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur sm:p-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">{featured.industry} · featured</p>
            <Quote className="mt-5 h-7 w-7 text-accent/70" />
            <blockquote className="mt-3 text-xl font-medium leading-relaxed tracking-tight sm:text-2xl">"{featured.quote}"</blockquote>
            <p className="mt-4 text-sm text-muted-foreground">— {featured.person}, {featured.name}</p>
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-border/50 pt-6">
              {featured.metrics.map((m) => (
                <div key={m.label}>
                  <div className="font-mono text-2xl font-semibold tracking-tight text-accent sm:text-3xl">{m.stat}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quotes */}
      <section className="pb-6">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-3">
          {quotes.map((q, i) => (
            <div key={i} className="rounded-2xl border border-border/60 bg-card/40 p-7 backdrop-blur">
              <Quote className="h-5 w-5 text-muted-foreground/60" />
              <p className="mt-3 text-sm leading-relaxed text-foreground/90">"{q.quote}"</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{q.person}</p>
            </div>
          ))}
        </div>
        <p data-testid="customers-partner-note" className="mx-auto mt-6 max-w-2xl px-6 text-center text-xs leading-relaxed text-muted-foreground">
          {CUSTOMERS.note}
        </p>
      </section>

      {/* Sectors */}
      <section className="border-t border-border/40 py-14">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">built with design partners across —</p>
        <div className="mx-auto mt-7 flex max-w-5xl flex-wrap items-center justify-center gap-x-10 gap-y-4 px-6 opacity-60">
          {logos.map((l) => <span key={l} className="font-mono text-sm font-medium text-muted-foreground">{l}</span>)}
        </div>
      </section>

      <section className="py-20 text-center">
        <h2 className="text-3xl font-semibold tracking-tight">See it on your data.</h2>
        <div className="mt-6 flex justify-center">
          <Link to="/contact"><Button className="rounded-full bg-accent px-6 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground hover:bg-accent/90">Book a walkthrough <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </PageShell>
  );
}
